import {
  ChangeEvent,
  Dispatch,
  ElementType,
  forwardRef,
  MouseEvent,
  Ref,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Box, Typography as T, Tooltip } from "@mui/material";
import {
  SimpleTreeView,
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2Icon,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Props,
  TreeItem2Provider,
  TreeItem2Root,
  // Imported from the package root, not the deep path, so the tree and its items
  // resolve to one module instance and therefore share a React context.
  useTreeItem2,
  useTreeItem2Utils,
} from "@mui/x-tree-view";
import useSchemaTables from "datasets/pages/tables/hooks/useSchemaTables";
import useTableSchemas from "datasets/pages/tables/hooks/useTableSchemas";
import { TreeNodeIcon } from "~/assets/icons";
import EmptyState from "~/components/EmptyState";
import Loading from "~/components/Loading";
import { useDebounce } from "~/hooks/useDebounce";
import { TableAccessLevel } from "~/pages/datasets/types/enums/access-level";
import { V1TableSearchResult } from "~/types/interfaces/catalogTables";
import { trackEvent } from "~/utils/monitoring";

// Synthetic item ids for the rows that are not tables. `::` cannot appear in a
// Snowflake FQN, so a table row's id is never mistaken for one of these.
const SYNTHETIC_ID_MARKER = "::";
const SCHEMA_ID_PREFIX = `schema${SYNTHETIC_ID_MARKER}`;
const schemaItemId = (schema: string) => `${SCHEMA_ID_PREFIX}${schema}`;
const schemaFromItemId = (itemId: string) => itemId.slice(SCHEMA_ID_PREFIX.length);
const helperItemId = (schema: string, kind: string) => `${kind}${SYNTHETIC_ID_MARKER}${schema}`;

/**
 * Only table rows carry a selection. Schema and helper rows have no checkbox, so a click
 * on them selects nothing — but a shift-click range or keyboard selection spans whatever
 * sits between its endpoints, so this keeps a synthetic id out of the submitted FQN list.
 */
const isTableItemId = (itemId: string) => !itemId.includes(SYNTHETIC_ID_MARKER);

// Explains a locked row on hover, since the disabled checkbox alone does not say why.
// Names the table so the reason is unambiguous when several rows are locked.
export const alreadyAssociatedMessage = (tableName: string) =>
  `${tableName} is already associated with the dataset.`;

interface TableRow {
  fqn: string;
  name: string;
  isAlreadyAdded: boolean;
}

/**
 * Decides how tables already in the dataset appear in the picker: listed, but locked.
 *
 * This is the single place that decision lives. Switching to hiding them instead means
 * changing this function *and* moving the exclusion server-side — filtering a page
 * client-side would shrink pages unevenly, leave schemas that expand to nothing, and
 * leave the schema counts wrong, since the aggregation counts every matching table
 * including the ones already added.
 */
const toRows = (tables: V1TableSearchResult[], addedFqns: Set<string>): TableRow[] =>
  tables.map((table) => ({
    fqn: table.fqn,
    name: table.name,
    isAlreadyAdded: addedFqns.has(table.fqn),
  }));

interface GroupTreeItemProps extends TreeItem2Props {
  icon?: ElementType;
}

/** An expandable row (a schema). Shows an expand toggle and icon, never a checkbox. */
const GroupTreeItem = forwardRef(function GroupTreeItem(
  props: GroupTreeItemProps,
  ref: Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, icon: Icon } = props;
  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps()}>
        <TreeItem2Content {...getContentProps()} sx={{ gap: 0.5 }}>
          <TreeItem2IconContainer {...getIconContainerProps()} sx={{ color: "grey.600" }}>
            <TreeItem2Icon status={status} />
          </TreeItem2IconContainer>
          {Icon && (
            <Box mr={0.5}>
              <Icon />
            </Box>
          )}
          <TreeItem2Label {...getLabelProps()} sx={{ fontSize: 14 }} />
        </TreeItem2Content>
        <TreeItem2GroupTransition {...getGroupTransitionProps()} />
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

interface LeafTreeItemProps extends TreeItem2Props {
  /** Render a selection checkbox. Table rows only. */
  hasCheckbox?: boolean;
  /** Show the checkbox checked regardless of the tree's selection state. */
  isChecked?: boolean;
  /** Explains the row on hover — why a locked row cannot be picked. */
  tooltip?: string;
  /** Called when a row without a checkbox is clicked (load more, retry). */
  onActivate?: () => void;
}

/**
 * A non-expandable row: a table, or a helper row such as "Load more" or a message.
 *
 * A table's whole row toggles its selection, not just its checkbox. Helper rows carry no
 * checkbox and act on a row click instead.
 */
const LeafTreeItem = forwardRef(function LeafTreeItem(
  props: LeafTreeItemProps,
  ref: Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, hasCheckbox, isChecked, tooltip, onActivate } =
    props;
  const { getRootProps, getContentProps, getCheckboxProps, getLabelProps, status } = useTreeItem2({
    id,
    itemId,
    children,
    label,
    disabled,
    rootRef: ref,
  });
  const { interactions } = useTreeItem2Utils({ itemId, children });

  const handleContentClick = (event: MouseEvent<HTMLElement>) => {
    if (onActivate) {
      onActivate();
      return;
    }
    // Unlike the checkbox's own change handler, handleCheckboxSelection does not itself
    // ignore disabled items, so a locked (already-added) row has to be stopped here.
    if (!hasCheckbox || status.disabled) {
      return;
    }
    // The checkbox raises its own change event. MUI's identical containment guard runs
    // only after this handler, so without this a direct click on the checkbox would
    // toggle twice and cancel out.
    if (event.target instanceof Node && getCheckboxProps().ref.current?.contains(event.target)) {
      return;
    }
    // Routed through MUI's own checkbox handler rather than our setSelectedItems so that
    // shift-click still works: this is what calls expandSelectionRange and maintains the
    // range anchor, and neither is reachable from the public API. It reads only
    // `nativeEvent.shiftKey` — present on a row click — and `target.checked`, whose
    // absence selectItem treats as "toggle", so a MouseEvent satisfies it.
    interactions.handleCheckboxSelection(event as unknown as ChangeEvent<HTMLInputElement>);
  };

  // Rows that do something on click ("Load more", "Retry") read as actions rather than as
  // another table: no checkbox column, accent colour, and link affordances. Derived from
  // `onActivate` so the styling cannot drift from whether the row is actually actionable.
  const isAction = Boolean(onActivate);

  const content = (
    <TreeItem2Content
      {...getContentProps({ onClick: handleContentClick })}
      sx={{ gap: 0.5, ...(isAction && { cursor: "pointer" }) }}
    >
      {hasCheckbox && (
        // One render path for every table row: the tree's own checkbox slot, which
        // zeroes the padding a plain MUI Checkbox would add (misaligning the label
        // and heightening the row). An already-added table only overrides `checked`;
        // its `disabled` already follows from the row's own disabled state.
        // Spread inline rather than via a local: TypeScript only infers the slot's
        // external props from the JSX context when the call is the spread itself.
        <TreeItem2Checkbox
          {...getCheckboxProps()}
          // Never `undefined`, which would override the spread and leave the
          // checkbox uncontrolled; `status.selected` is what the slot already uses.
          checked={isChecked || status.selected}
          size="small"
        />
      )}
      <TreeItem2Label
        {...getLabelProps()}
        sx={{
          fontSize: 14,
          ...(isAction && {
            color: "primary.main",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }),
        }}
      />
    </TreeItem2Content>
  );

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps()}>
        {/* Wraps the whole row, not just its text, so hovering anywhere on a locked row
            explains it. A disabled tree item only dims — MUI does not stop its pointer
            events — so the tooltip fires without any workaround. `describeChild` keeps the
            row's accessible name as the table name; without it MUI applies the message as
            an aria-label and a screen reader loses which table the row is. */}
        {tooltip ? (
          <Tooltip title={tooltip} describeChild>
            {content}
          </Tooltip>
        ) : (
          content
        )}
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

interface SchemaTablesProps {
  schema: string;
  searchTerm: string;
  addedFqns: Set<string>;
  accessLevel?: TableAccessLevel;
  datasetId?: string;
}

/**
 * The tables inside one expanded schema, paged on demand.
 *
 * Mounted only while its schema is expanded, so a collapsed schema costs no request.
 * This is why the tree renders JSX items rather than a flat item array — each schema
 * owns its own data hook, which a single hook over a dynamic set of schemas could not.
 */
const SchemaTables = ({
  schema,
  searchTerm,
  addedFqns,
  accessLevel,
  datasetId,
}: SchemaTablesProps) => {
  const { items, hasMore, isLoading, isLoadingMore, error, loadMore, retry, remainingCount } =
    useSchemaTables(schema, searchTerm, accessLevel);

  if (isLoading) {
    return (
      <LeafTreeItem itemId={helperItemId(schema, "loading")} label="Loading tables…" disabled />
    );
  }

  if (error) {
    return (
      <LeafTreeItem
        itemId={helperItemId(schema, "error")}
        label="Could not load tables. Retry"
        onActivate={retry}
      />
    );
  }

  const rows = toRows(items, addedFqns);

  if (!rows.length) {
    return <LeafTreeItem itemId={helperItemId(schema, "empty")} label="No tables found" disabled />;
  }

  return (
    <>
      {rows.map((row) => (
        <LeafTreeItem
          key={row.fqn}
          itemId={row.fqn}
          label={row.name}
          hasCheckbox
          // Already in the dataset: shown checked so its state is obvious, disabled so it
          // cannot be submitted again, and explained on hover.
          isChecked={row.isAlreadyAdded}
          disabled={row.isAlreadyAdded}
          tooltip={row.isAlreadyAdded ? alreadyAssociatedMessage(row.name) : undefined}
        />
      ))}
      {hasMore && (
        <LeafTreeItem
          itemId={helperItemId(schema, "loadmore")}
          label={
            isLoadingMore ? "Loading…" : `Load more (${remainingCount.toLocaleString()} remaining)`
          }
          // While a page is in flight the row is inert: dropping `onActivate` stops it
          // reading as an action and keeps a second click from advancing past the page
          // still loading. Deliberately not `disabled` — MUI re-registers an item whenever
          // that prop changes, and it moves focus to the first row when the focused item
          // is removed, which scrolled the picker back to the top on every click.
          onActivate={
            isLoadingMore
              ? undefined
              : () => {
                  // `loaded_count` is how many were already on screen, so paging depth is
                  // visible without having to reconstruct it from repeated events.
                  trackEvent("dataset_table_picker_load_more_clicked", {
                    dataset_id: datasetId,
                    schema,
                    loaded_count: items.length,
                  });
                  loadMore();
                }
          }
        />
      )}
    </>
  );
};

const TableTreeView = ({
  searchTerm,
  selectedItems,
  setSelectedItems,
  accessLevel,
  addedFqns,
  datasetId,
}: {
  searchTerm: string;
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  /** FQNs already in the dataset; undefined until that list has loaded. */
  addedFqns?: Set<string>;
  accessLevel?: TableAccessLevel;
  /** Segments the picker's analytics events. Passed in so the tree stays route-agnostic. */
  datasetId?: string;
}) => {
  // Debounced once here, then shared by the schema probe and every expanded schema, so
  // they can never disagree about which term is current.
  const debouncedSearch = useDebounce(searchTerm);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { schemas, hasOmittedSchemas, isLoading } = useTableSchemas(debouncedSearch, accessLevel);

  // A fire-and-forget analytics ping on the settled term, so a search counts once rather
  // than once per keystroke. Only a real term counts — opening the picker with an empty
  // box is already covered by its own event.
  useEffect(() => {
    const term = debouncedSearch.trim();
    if (!term) {
      return;
    }
    trackEvent("dataset_table_picker_searched", {
      dataset_id: datasetId,
      // The term itself is deliberately not sent; its length still shows whether people
      // type a couple of characters or a full table name.
      term_length: term.length,
    });
  }, [debouncedSearch, datasetId]);

  // Wait for the dataset's own tables before rendering rows — without them, a table
  // already in the dataset would briefly look addable. The schema probe above still
  // starts immediately, so this costs no round trip.
  if (isLoading || !addedFqns) {
    return <Loading />;
  }

  if (!schemas.length) {
    return <EmptyState label="No tables found" />;
  }

  return (
    <>
      {hasOmittedSchemas && (
        <T variant="caption" color="text.secondary" px={1}>
          Showing the first {schemas.length.toLocaleString()} schemas. Search to narrow the list.
        </T>
      )}
      <SimpleTreeView
        multiSelect
        checkboxSelection
        itemChildrenIndentation={20}
        expandedItems={expandedItems}
        onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
        onItemExpansionToggle={(_event, itemId, isExpanded) => {
          if (!isExpanded) {
            return;
          }
          // `searched` separates browsing the schema list from drilling into a search hit.
          trackEvent("dataset_table_picker_schema_expanded", {
            dataset_id: datasetId,
            schema: schemaFromItemId(itemId),
            searched: debouncedSearch.trim().length > 0,
          });
        }}
        selectedItems={selectedItems}
        onItemSelectionToggle={(_event, itemId, isSelected) => {
          if (!isTableItemId(itemId)) {
            return;
          }
          if (isSelected) {
            setSelectedItems((prev) => Array.from(new Set([...prev, itemId])));
          } else {
            setSelectedItems((prev) => prev.filter((fqn) => fqn !== itemId));
          }
        }}
      >
        {schemas.map((schema) => {
          const itemId = schemaItemId(schema.name);
          return (
            <GroupTreeItem
              key={itemId}
              itemId={itemId}
              icon={TreeNodeIcon}
              label={`${schema.name} (${schema.tableCount.toLocaleString()})`}
            >
              {expandedItems.includes(itemId) ? (
                <SchemaTables
                  schema={schema.name}
                  searchTerm={debouncedSearch}
                  addedFqns={addedFqns}
                  accessLevel={accessLevel}
                  datasetId={datasetId}
                />
              ) : (
                // A collapsed schema still needs a child, since MUI derives
                // expandability from whether children exist. Never visible: expanding
                // swaps it for SchemaTables in the same commit.
                <LeafTreeItem
                  itemId={helperItemId(schema.name, "placeholder")}
                  label="Loading tables…"
                  disabled
                />
              )}
            </GroupTreeItem>
          );
        })}
      </SimpleTreeView>
    </>
  );
};

export default TableTreeView;
